/**
 * Back-office: sidebar agrupado (Operación / Planificación / Configuración /
 * Cumplimiento) en escritorio; en móvil la navegación se vuelve una barra
 * horizontal deslizable arriba.
 *
 * Los contadores de pendientes son REALES. El layout anterior llevaba
 * `punches: 5, documents: 3` hardcodeados con un comentario que decía
 * "these would be real API calls" — un admin veía "5 pendientes" siempre,
 * hubiera o no. Eso estuvo en producción.
 */
import { ReactNode, useEffect, useState } from 'react';
import { TrialBanner } from '../ui/TrialBanner';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import {
  getPendingCorrections,
  getPendingPunches,
  getPendingVacations,
  getActivityAnomalies,
} from '../../services/apiClient';
import { Icon, IconName } from '../ui/Icon';
import { ThemeToggle } from '../ui/ThemeToggle';

interface NavItem {
  path: string;
  icon: IconName;
  label: string;
  countKey?: keyof Counts;
  roles: ('ADMIN' | 'MANAGER')[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

/**
 * Contador de un ítem del menú:
 * - `undefined`: aún sin cargar (no se pinta nada, como siempre).
 * - `null`: la llamada falló — se pinta «—» con el motivo en el title, NUNCA
 *   0: un 401/5xx puntual dejaba el badge a cero con aspecto de «sin
 *   pendientes» hasta el refresco de los 2 minutos (ronda 3, r2b20[6]).
 * - número: el conteo real.
 */
type Count = number | null | undefined;

interface Counts {
  corrections?: Count;
  pending?: Count;
  vacations?: Count;
  anomalies?: Count;
}

const GROUPS: NavGroup[] = [
  {
    title: 'Operación',
    items: [
      { path: '/admin/inbox', icon: 'edit', label: 'Correcciones', countKey: 'corrections', roles: ['ADMIN', 'MANAGER'] },
      { path: '/admin/pending', icon: 'alert', label: 'Marcas a revisión', countKey: 'pending', roles: ['ADMIN', 'MANAGER'] },
      { path: '/admin/anomalies', icon: 'alert', label: 'Anomalías', countKey: 'anomalies', roles: ['ADMIN', 'MANAGER'] },
      { path: '/admin/vacations', icon: 'palm', label: 'Vacaciones', countKey: 'vacations', roles: ['ADMIN', 'MANAGER'] },
    ],
  },
  {
    title: 'Planificación',
    items: [
      { path: '/admin/rotations', icon: 'clock', label: 'Rotaciones', roles: ['ADMIN', 'MANAGER'] },
      { path: '/admin/planning', icon: 'calendar', label: 'Cuadrante', roles: ['ADMIN', 'MANAGER'] },
    ],
  },
  {
    title: 'Configuración',
    items: [
      { path: '/admin/company', icon: 'building', label: 'Empresa', roles: ['ADMIN'] },
      { path: '/admin/worksites', icon: 'pin', label: 'Centros', roles: ['ADMIN'] },
      { path: '/admin/workers', icon: 'kiosk', label: 'Trabajadores', roles: ['ADMIN'] },
      { path: '/admin/policy', icon: 'shield', label: 'Convenio', roles: ['ADMIN'] },
      { path: '/admin/calendar', icon: 'calendar', label: 'Calendario', roles: ['ADMIN'] },
    ],
  },
  {
    title: 'Cumplimiento',
    items: [
      { path: '/admin/overtime', icon: 'clock', label: 'Horas extra', roles: ['ADMIN', 'MANAGER'] },
      { path: '/admin/documents', icon: 'file', label: 'Documentos', roles: ['ADMIN'] },
      { path: '/admin/report', icon: 'clipboard', label: 'Informe Inspección', roles: ['ADMIN'] },
      { path: '/admin/audit', icon: 'info', label: 'Auditoría', roles: ['ADMIN'] },
      { path: '/admin/api-keys', icon: 'dots', label: 'Claves de API', roles: ['ADMIN'] },
    ],
  },
];

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
}

export function AdminLayout({ children, title, subtitle }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAppStore();
  const [counts, setCounts] = useState<Counts>({});

  useEffect(() => {
    const fetchCounts = async () => {
      // Cada contador cae por su lado: si una llamada falla, las otras siguen.
      // La que falla se marca a null («—» en el badge), no a 0 (r2b20[6]).
      const [corr, pend, vac, anom] = await Promise.all([
        getPendingCorrections().catch(() => null),
        getPendingPunches().catch(() => null),
        getPendingVacations().catch(() => null),
        getActivityAnomalies().catch(() => null),
      ]);
      setCounts({
        corrections: corr?.total ?? null,
        pending: pend?.length ?? null,
        vacations: vac?.length ?? null,
        anomalies: anom?.length ?? null,
      });
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const role = user?.role as 'ADMIN' | 'MANAGER' | undefined;
  const groups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => role && i.roles.includes(role)),
  })).filter((g) => g.items.length > 0);

  const crumb = groups.find((g) => g.items.some((i) => i.path === location.pathname))?.title ?? '';

  const navButton = (item: NavItem) => {
    const active = location.pathname === item.path;
    const count = item.countKey ? counts[item.countKey] : 0;
    return (
      <button
        key={item.path}
        onClick={() => navigate(item.path)}
        aria-current={active ? 'page' : undefined}
        className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14.5px] text-left transition-colors whitespace-nowrap ${
          active ? 'bg-brand-soft text-brand font-bold' : 'text-ink-2 font-medium hover:bg-surface-2'
        }`}
      >
        <Icon name={item.icon} size={19} strokeWidth={1.7} className="shrink-0" />
        <span className="flex-1">{item.label}</span>
        {count === null ? (
          // Fallo de carga: «—» con el motivo accesible. Nunca un 0 que
          // parezca «sin pendientes» (r2b20[6]).
          <span
            className="shrink-0 font-mono text-[11px] font-bold text-ink-3"
            title="No se pudo cargar el contador de pendientes (error de la API)"
            aria-label="Contador de pendientes no disponible: error de carga"
          >
            —
          </span>
        ) : (
          count != null && count > 0 && (
            <span className="shrink-0 font-mono text-[11px] font-bold bg-signal text-white rounded-full min-w-[20px] h-5 grid place-items-center px-1.5">
              {count}
            </span>
          )
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-canvas grid md:grid-cols-[246px_1fr]">
      {/* Sidebar (escritorio) / barra superior (móvil) */}
      <aside className="bg-surface border-b md:border-b-0 md:border-r border-line md:h-screen md:sticky md:top-0 flex md:flex-col items-center md:items-stretch sticky top-0 z-10">
        <div className="flex items-center gap-2 px-4 py-3.5 md:border-b border-line shrink-0">
          <span className="w-3 h-3 rounded-full bg-brand ring-4 ring-brand-soft" />
          <span className="font-display font-black text-[20px] tracking-tight">kakuat</span>
          <span className="hidden md:inline font-mono text-[9.5px] text-ink-3 border border-line-strong rounded px-1.5 py-0.5 ml-auto">
            {role}
          </span>
        </div>

        <nav className="flex md:flex-col gap-1 md:gap-0.5 px-2.5 md:py-3 overflow-x-auto md:overflow-y-auto md:flex-1 items-center md:items-stretch">
          {/* Un admin también ficha: salida directa a la app del trabajador. */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] font-semibold text-ink-2 hover:bg-surface-2 whitespace-nowrap shrink-0"
          >
            <Icon name="clock" size={18} strokeWidth={1.8} />
            <span className="hidden md:inline">Ir a fichar</span>
          </button>
          {groups.map((g) => (
            <div key={g.title} className="flex md:block gap-1">
              <p className="hidden md:block font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3 px-2.5 pt-3.5 pb-1.5">
                {g.title}
              </p>
              <div className="flex md:flex-col gap-1 md:gap-0.5">{g.items.map(navButton)}</div>
            </div>
          ))}
        </nav>

        <div className="hidden md:block border-t border-line p-3.5">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-brand-soft text-brand grid place-items-center font-display font-extrabold text-[13px] shrink-0">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[13.5px] truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="text-[12px] text-ink-3 hover:text-ink"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Contenido */}
      <div className="min-w-0 flex flex-col">
        <header className="sticky top-[57px] md:top-0 z-[9] bg-canvas/90 backdrop-blur border-b border-line px-5 md:px-8 py-3.5 flex items-center gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[11px] uppercase tracking-wider text-ink-3">{crumb}</p>
            <h1 className="font-display font-extrabold text-[22px] md:text-[26px] tracking-tight truncate">
              {title}
            </h1>
            {subtitle && <p className="text-[13px] text-ink-2">{subtitle}</p>}
          </div>
          <div className="ml-auto shrink-0">
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 p-4 md:p-7 overflow-x-hidden">
          <div className="mb-4 empty:hidden"><TrialBanner /></div>
          {children}
        </main>
      </div>
    </div>
  );
}
